import React, { ReactNode } from 'react';
import styled, { withTheme, css, DefaultTheme } from 'styled-components';

interface HeroImg {
  src: string;
  alt: string;
  width?: string;
}

interface ProductInfo {
  logoSvg: string;
  name: string;
}

interface ProductSplashProps {
  header: ReactNode;
  description: ReactNode;
  additionalContent?: ReactNode;
  heroImg: HeroImg;
  buttons?: ReactNode;
  productInfo: ProductInfo;
  licenseInfoLink?: ReactNode;
}

const ProductSplash = ({
  header,
  description,
  additionalContent,
  heroImg,
  buttons,
  productInfo,
  licenseInfoLink,
}: ProductSplashProps): React.JSX.Element => {
  const { logoSvg, name } = productInfo;
  const showAddonContent = !!additionalContent;
  return (
    <TabContainer>
      <MainGrid showAddonContent={showAddonContent}>
        <HeaderContainer>
          <Header>{header}</Header>
          <Description>{description}</Description>
        </HeaderContainer>
        <HeroImageContainer showAddonContent={showAddonContent}>
          <HeroImage src={heroImg.src} alt={heroImg.alt} width={heroImg.width} />
        </HeroImageContainer>
        {showAddonContent && <AdditionalContent>{additionalContent}</AdditionalContent>}
        {buttons && (
          <ProductLogoContainer>
            <ProductLogo src={logoSvg} alt={`${name} logo`} />
          </ProductLogoContainer>
        )}
        {buttons && (
          <ButtonContainer>
            {licenseInfoLink}
            {buttons}
          </ButtonContainer>
        )}
      </MainGrid>
    </TabContainer>
  );
};

export default withTheme(ProductSplash);

const minWidth =
  (size: string) =>
    ({ theme: { minWidth: minWidthFunc } }: { theme: DefaultTheme }): string =>
      minWidthFunc(size);

const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 100%;
`;

const gridLayoutDefault = css`
  grid-template-areas:
    'header logo'
    'hero buttons'
    'addons buttons';
  grid-template-columns: 4fr 1fr;
  grid-template-rows: 1fr fit-content 4fr;

  ${minWidth('xl')} {
    overflow: none;
    grid-template-areas:
      'header header logo'
      'hero addons buttons';
    grid-template-columns: 3fr 1fr 1fr;
    grid-template-rows: 1fr fit-content;
`;

const gridLayoutNoAddonContent = css`
  grid-template-areas:
    'header logo'
    'hero buttons';
  grid-template-columns: 2fr 1fr;
`;

const MainGrid = styled.div<{ showAddonContent: boolean }>`
  display: grid;
  column-gap: ${({ theme }) => theme.grid.units(1)};
  margin: 10px 0 20px;
  ${({ showAddonContent }) => (showAddonContent ? gridLayoutDefault : gridLayoutNoAddonContent)};
`;

const HeaderContainer = styled.div`
  grid-area: header;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding: 0 ${({ theme }) => theme.grid.units(2)};

  ${minWidth('xl')} {
    padding: 0 ${({ theme }) => theme.grid.units(4)};
  }
`;

const Header = styled.h1`
  color: ${({ theme }) => theme.colors.productTitle};
  font-size: 2.4em;
  font-weight: 600;
  text-align: center;
  margin: 0.5em 0 0.25em 0;
`;

const Description = styled.div`
  color: ${({ theme }) => theme.colors.text};
  font-size: 18px;
  text-align: center;
  margin-top: 10px;
  margin-bottom: 0.2em;
`;

const imageSizeDefault = css`
  width: 100%;
`;

const imageSizeNoAddonContent = css`
  width: 100%;
`;

const HeroImageContainer = styled.div<{ showAddonContent: boolean }>`
  grid-area: hero;
  margin: auto;
  transition: width 0.2s;
  ${({ showAddonContent }) => (showAddonContent ? imageSizeDefault : imageSizeNoAddonContent)};
`;

const AdditionalContent = styled.div`
  display: flex;
  justify-content: space-between;
  grid-area: addons;
  font-size: 16px;
  padding: 0 ${({ theme }) => theme.grid.units(2)};
  margin-top: 10px 10px;
  flex-wrap: wrap;

  & > div {
    flex: 1 1 0;
    padding-left: 0px;
    padding-right: 30px;

    &:first-child {
      padding-left: 60px;
    }
  }

  h1 {
    text-align: center;
    width: 100%;
    display: block;
    font-size: 1.6em;
    font-weight: 600;
    padding-bottom: 1.2em;
    color: ${({ theme }) => theme.colors.flowjoBlue};
  }

  ${minWidth('xl')} {
    display: block;
    padding-right: 30px;
    padding-left: 0px;
    margin-right: 0px;
    margin-top: 0px;
    & > div {
      padding-left: 0px;
    }
  }

  b {
    color: ${({ theme }) => theme.colors.productTitle};
  }

  p {
    font-size: 16px;
    margin-top: 7px;
    margin-bottom: 20px;
  }
`;
/* add a default width prop to HeroImage */
/* center the image horizontally in the div */
const HeroImage = styled.img<{ width?: string }>`
  width: ${({ width }) => width || '100%'};
  padding: 15px 0;
  display: block;
  margin: 0 auto;
`;

const ProductLogoContainer = styled.div`
  display: flex;
  grid-area: logo;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  height: 100%;
  width: 100%;
  padding-bottom: 30px;
`;

const ProductLogo = styled.img`
  min-width: 130px;
  min-height: 30px;
  height: 50px;
  align-self: flex-begin;
`;

const ButtonContainer = styled.div`
  grid-area: buttons;
  flex: 0 0 100px;
  min-width: fit-content;
`;
